import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Upload, Camera, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Uploads files to /api/files/upload, stores returned ids.
 * Two entry modes: Camera (uses capture="environment" — opens camera on mobile,
 * standard file picker on desktop) + File (any file).
 */
export default function FileUploader({
  ids = [],
  onChange,
  category = "misc",
  label = "Photos",
  accept = "image/*",
  multiple = true,
}) {
  const fileRef = useRef();
  const camRef = useRef();
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const newIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.id);
      }
      onChange([...ids, ...newIds]);
      toast.success(`Uploaded ${newIds.length}`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const remove = (id) => onChange(ids.filter((i) => i !== id));

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isImageAccept = accept.includes("image");

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="kk-label m-0">{label}</span>
        <div className="flex gap-2">
          {isImageAccept && (
            <button
              type="button"
              onClick={() => camRef.current?.click()}
              disabled={busy}
              className="kk-pill kk-pill-black"
              data-testid={`camera-${category}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
              Camera
            </button>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="kk-pill kk-pill-black"
            data-testid={`upload-${category}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Files
          </button>
        </div>
        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple={false}
          onChange={handle}
          className="hidden"
        />
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handle}
          className="hidden"
        />
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {ids.map((id) => (
          <div key={id} className="relative aspect-square border border-[var(--kk-border)] bg-[var(--kk-surface-2)] overflow-hidden">
            {isImageAccept ? (
              <img src={`${BACKEND_URL}/api/files/${id}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <a href={`${BACKEND_URL}/api/files/${id}`} target="_blank" rel="noreferrer" className="flex items-center justify-center h-full text-xs font-mono p-2 text-center">
                doc · {id.slice(0, 6)}
              </a>
            )}
            <button
              type="button"
              onClick={() => remove(id)}
              className="absolute top-1 right-1 bg-white/90 p-1 border border-[var(--kk-border)]"
              aria-label="remove"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {ids.length === 0 && (
          <div className="col-span-full text-xs text-[var(--kk-muted)] py-4 border border-dashed border-[var(--kk-border)] text-center">
            No files yet
          </div>
        )}
      </div>
    </div>
  );
}
